import React from "react";
import { Link } from "react-router-dom";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import { Translate, I18n } from "react-redux-i18n";
import QRCode from "qrcode.react";
import alertify from "alertifyjs";
import { FaArrowLeft } from "react-icons/fa";

import "./styles/Profile.css";
import "../components/styles/Alertify.css";
import "../components/styles/AlertifyTheme.css";
import "../components/styles/Alertify.min.css";
import "../components/styles/AlertifyTheme.min.css";
import ProfileLayout from "../components/ProfileLayout";
import { ListSkeleton } from "../components/Skeleton";
import MiniLoader from "../components/MiniLoader";
import {
  LOCALE_BROWSER,
  getLocalePreference,
} from "../assets/lang/localeUtils";
import { supportedLocales } from "../assets/lang/supportedLang";
import {
  THEME_LIGHT,
  THEME_DARK,
  getThemePreference,
  applyThemePreference,
} from "../assets/lang/themeUtils";
import {
  TWO_FACTOR_METHOD_SMS,
  TWO_FACTOR_METHOD_EMAIL,
  TWO_FACTOR_METHOD_AUTHENTICATOR,
  TWO_FACTOR_DEFAULT_METHOD,
} from "../assets/lang/twoFactorUtils";
import { setLocalePreferenceAndApply } from "../redux/actions/i18n";
import * as twoFactorActions from "../redux/actions/twoFactorActions";
import * as sessionActions from "../redux/actions/sessionActions";
import UserVerificationPanel from "../components/UserVerificationPanel";

const languageOptions = [
  { value: LOCALE_BROWSER, labelKey: "profile_configuration_language_browser" },
  { value: "en", label: supportedLocales.en },
  { value: "es", label: supportedLocales.es },
  { value: "he", label: supportedLocales.he },
];

const themeOptions = [
  { value: THEME_LIGHT, labelKey: "profile_configuration_theme_light" },
  { value: THEME_DARK, labelKey: "profile_configuration_theme_dark" },
];

const twoFactorMethodOptions = [
  { value: TWO_FACTOR_METHOD_EMAIL, labelKey: "profile_configuration_2fa_email" },
  {
    value: TWO_FACTOR_METHOD_SMS,
    labelKey: "profile_configuration_2fa_sms",
    comingSoon: true,
  },
  {
    value: TWO_FACTOR_METHOD_AUTHENTICATOR,
    labelKey: "profile_configuration_2fa_authenticator",
  },
];

function ConfigOption({ name, value, checked, onChange, children, disabled }) {
  return (
    <label
      className={`profile-config-option${
        checked ? " profile-config-option--selected" : ""
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange(value)}
      />
      <span className="profile-config-option__label">{children}</span>
    </label>
  );
}

class ProfileConfiguration extends React.Component {
  state = {
    localePreference: getLocalePreference(),
    themePreference: getThemePreference(),
    activating2FA: false,
    verificationCode: "",
    deletePassword: "",
    deletingAccount: false,
    ready: false,
  };

  componentDidMount() {
    this.fetchTwoFactor();
  }

  fetchTwoFactor = async () => {
    await this.props.LoadTwoFactor(this.props.user_id, this.props.token);
    this.setState({ ready: true });
  };

  getContactEmail = () =>
    this.props.twoFactorSettings.email || this.props.userEmail || "";

  getContactPhone = () =>
    this.props.twoFactorSettings.phone || this.props.userPhone || "";

  handleLanguageChange = (event) => {
    const preference = event.target.value;
    this.setState({ localePreference: preference });
    this.props.setLocalePreferenceAndApply(preference);
  };

  handleThemeChange = (preference) => {
    this.setState({ themePreference: preference });
    applyThemePreference(preference);
  };

  handleTwoFactorToggle = async () => {
    const { twoFactorSettings } = this.props;

    if (twoFactorSettings.enabled) {
      await this.props.UpdateTwoFactor({
        user_id: this.props.user_id,
        token: this.props.token,
        enabled: false,
        method: twoFactorSettings.method,
      }).then(() => {
        if (this.props.twoFactorErrorUpdate) {
          alertify.warning(I18n.t(this.props.twoFactorErrorUpdate));
        }
        this.setState({ activating2FA: false, verificationCode: "" });
      });
      return;
    }

    if (this.state.activating2FA) {
      this.setState({ activating2FA: false, verificationCode: "" });
      return;
    }

    this.setState({
      activating2FA: true,
      verificationCode: "",
    });
  };

  handleTwoFactorMethodChange = (method) => {
    this.setState({ verificationCode: "" });
    this.props.UpdateTwoFactor({
      user_id: this.props.user_id,
      token: this.props.token,
      enabled: false,
      method,
      email: this.getContactEmail(),
      phone: this.getContactPhone(),
    }).then(() => {
      if (this.props.twoFactorErrorUpdate) {
        alertify.warning(I18n.t(this.props.twoFactorErrorUpdate));
      }
    });
  };

  handleTwoFactorAction = async () => {
    const { twoFactorSettings } = this.props;
    const method = twoFactorSettings.method || TWO_FACTOR_DEFAULT_METHOD;

    if (method === TWO_FACTOR_METHOD_AUTHENTICATOR) {
      await this.props.SetupTwoFactorAuthenticator(
        this.props.user_id,
        this.props.token
      ).then(() => {
        if (this.props.twoFactorErrorUpdate) {
          alertify.warning(I18n.t(this.props.twoFactorErrorUpdate));
        }
      });
      return;
    }

    if (method === TWO_FACTOR_METHOD_SMS && !this.getContactPhone()) {
      alertify.warning(I18n.t("profile_configuration_2fa_no_phone"));
      return;
    }

    if (method === TWO_FACTOR_METHOD_EMAIL && !this.getContactEmail()) {
      alertify.warning(I18n.t("profile_configuration_2fa_no_email"));
      return;
    }

    await this.props.SendTwoFactorCode({
      user_id: this.props.user_id,
      token: this.props.token,
      method,
    }).then(() => {
      if (this.props.twoFactorErrorUpdate) {
        alertify.warning(I18n.t(this.props.twoFactorErrorUpdate));
        return;
      }
      alertify.success(I18n.t("profile_configuration_2fa_code_sent"));
    });
  };

  handleVerifyTwoFactor = async () => {
    if (!this.state.verificationCode.trim()) {
      alertify.warning(I18n.t("profile_configuration_2fa_code_required"));
      return;
    }

    await this.props.VerifyTwoFactor({
      user_id: this.props.user_id,
      token: this.props.token,
      code: this.state.verificationCode.trim(),
    }).then(() => {
      if (this.props.twoFactorErrorUpdate) {
        alertify.warning(I18n.t(this.props.twoFactorErrorUpdate));
        return;
      }

      alertify.success(I18n.t("profile_configuration_2fa_enabled_success"));
      this.setState({ activating2FA: false, verificationCode: "" });
    });
  };

  handleDeleteAccount = () => {
    if (!this.state.deletePassword.trim()) {
      alertify.warning(I18n.t("profile_configuration_delete_password_required"));
      return;
    }

    alertify
      .confirm(
        I18n.t("warning"),
        I18n.t("profile_configuration_delete_confirm"),
        async () => {
          this.setState({ deletingAccount: true });

          const result = await this.props.DeleteAccount({
            user_id: this.props.user_id,
            token: this.props.token,
            password: this.state.deletePassword.trim(),
          });

          this.setState({ deletingAccount: false });

          if (!result?.success) {
            alertify.warning(
              I18n.t(result?.error || this.props.sessionErrorUpdate || "try_later")
            );
            return;
          }

          alertify.success(I18n.t("profile_configuration_delete_success"));
          this.props.history.push("/login");
        },
        () => {}
      )
      .set("labels", {
        ok: I18n.t("profile_configuration_delete_btn"),
        cancel: I18n.t("profile_configuration_delete_cancel"),
      });
  };

  getTwoFactorActionLabel = () => {
    const method =
      this.props.twoFactorSettings.method || TWO_FACTOR_DEFAULT_METHOD;

    if (method === TWO_FACTOR_METHOD_SMS) {
      return I18n.t("profile_configuration_2fa_send_sms");
    }

    if (method === TWO_FACTOR_METHOD_AUTHENTICATOR) {
      return I18n.t("profile_configuration_2fa_generate_qr");
    }

    return I18n.t("profile_configuration_2fa_send_email");
  };

  renderTwoFactorMethodPanel = () => {
    const { twoFactorSettings, codeSent, qrReady, twoFactorLoading } =
      this.props;
    const method = twoFactorSettings.method || TWO_FACTOR_DEFAULT_METHOD;
    const showVerification =
      codeSent || (qrReady && twoFactorSettings.otpauthUrl);

    return (
      <div className="profile-config-2fa-panel">
        {method === TWO_FACTOR_METHOD_EMAIL && (
          <div className="profile-config-contact">
            <span className="profile-config-contact__label">
              <Translate value="profile_configuration_2fa_email_label" />
            </span>
            <div className="profile-config-contact__value">
              {this.getContactEmail() || "—"}
            </div>
          </div>
        )}

        {method === TWO_FACTOR_METHOD_SMS && (
          <div className="profile-config-contact">
            <span className="profile-config-contact__label">
              <Translate value="profile_configuration_2fa_phone_label" />
            </span>
            <div className="profile-config-contact__value">
              {this.getContactPhone() || "—"}
            </div>
            {!this.getContactPhone() && (
              <p className="profile-config-contact__hint">
                <Translate value="profile_configuration_2fa_no_phone" />
              </p>
            )}
          </div>
        )}

        {method === TWO_FACTOR_METHOD_AUTHENTICATOR &&
          qrReady &&
          twoFactorSettings.otpauthUrl && (
            <div className="profile-config-qr">
              <p className="profile-config-qr__hint">
                <Translate value="profile_configuration_2fa_scan_qr" />
              </p>
              <QRCode value={twoFactorSettings.otpauthUrl} size={180} />
            </div>
          )}

        <div className="profile-config-actions">
          <button
            type="button"
            className="profile-config-btn"
            onClick={this.handleTwoFactorAction}
            disabled={twoFactorLoading}
          >
            {this.getTwoFactorActionLabel()}
          </button>
        </div>

        {showVerification && (
          <div className="profile-config-verify-row">
            <input
              type="text"
              className="form-control profile-edit-input"
              maxLength={6}
              placeholder={I18n.t("profile_configuration_2fa_code_placeholder")}
              value={this.state.verificationCode}
              onChange={(event) =>
                this.setState({ verificationCode: event.target.value })
              }
            />
            <button
              type="button"
              className="profile-config-btn profile-config-btn--success"
              onClick={this.handleVerifyTwoFactor}
              disabled={twoFactorLoading}
            >
              <Translate value="profile_configuration_2fa_verify" />
            </button>
          </div>
        )}
      </div>
    );
  };

  render() {
    const { twoFactorSettings, twoFactorLoading } = this.props;
    const { ready } = this.state;
    const showTwoFactorSetup =
      this.state.activating2FA || twoFactorSettings.enabled;
    const selectedMethod =
      twoFactorSettings.method || TWO_FACTOR_DEFAULT_METHOD;
    const loading = !ready && twoFactorLoading;

    return (
      <ProfileLayout>
        <div className="profile-data-page profile-edit-page">
          <div className="card profile-edit-card">
            <div className="profile-edit-header profile-data-header">
              <Link to="/profile" className="profile-edit-back" aria-label="Back">
                <FaArrowLeft />
              </Link>
              <div>
                <h3 className="profile-edit-title">
                  <Translate value="profile_configuration" />
                </h3>
                <p className="profile-edit-subtitle">
                  @{this.props.profile?.username || ""}
                </p>
              </div>
            </div>

            {loading ? (
              <div className="profile-edit-form">
                <ListSkeleton />
              </div>
            ) : (
              <div className="profile-edit-form">
                <section className="profile-data-section">
                  <h4 className="profile-data-section__title">
                    <Translate value="profile_configuration_language" />
                  </h4>
                  <select
                    className="form-control profile-edit-input"
                    name="language"
                    value={this.state.localePreference}
                    onChange={this.handleLanguageChange}
                  >
                    {languageOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.labelKey
                          ? I18n.t(option.labelKey)
                          : option.label}
                      </option>
                    ))}
                  </select>
                </section>

                <section className="profile-data-section">
                  <h4 className="profile-data-section__title">
                    <Translate value="profile_configuration_theme" />
                  </h4>
                  <div className="profile-config-option-list profile-config-option-list--row">
                    {themeOptions.map((option) => (
                      <ConfigOption
                        key={option.value}
                        name="theme"
                        value={option.value}
                        checked={this.state.themePreference === option.value}
                        onChange={this.handleThemeChange}
                      >
                        <Translate value={option.labelKey} />
                      </ConfigOption>
                    ))}
                  </div>
                </section>

                <section className="profile-data-section">
                  <h4 className="profile-data-section__title">
                    <Translate value="profile_configuration_2fa" />
                  </h4>

                  <div className="profile-edit-toggle profile-config-2fa-toggle">
                    <div>
                      <strong>
                        <Translate value="profile_configuration_2fa_enable" />
                        {twoFactorSettings.enabled && (
                          <span className="profile-config-badge">
                            <Translate value="profile_configuration_2fa_active" />
                          </span>
                        )}
                      </strong>
                    </div>
                    <label className="profile-edit-switch">
                      <input
                        type="checkbox"
                        checked={
                          Boolean(twoFactorSettings.enabled) ||
                          this.state.activating2FA
                        }
                        disabled={twoFactorLoading}
                        onChange={this.handleTwoFactorToggle}
                      />
                      <span className="profile-edit-switch-slider" />
                    </label>
                  </div>

                  {showTwoFactorSetup && !twoFactorSettings.enabled && (
                    <>
                      <p className="profile-config-section-hint">
                        <Translate value="profile_configuration_2fa_method" />
                      </p>
                      <div className="profile-config-option-list">
                        {twoFactorMethodOptions.map((option) => (
                          <ConfigOption
                            key={option.value}
                            name="twoFactorMethod"
                            value={option.value}
                            checked={
                              !option.comingSoon &&
                              selectedMethod === option.value
                            }
                            disabled={twoFactorLoading || option.comingSoon}
                            onChange={this.handleTwoFactorMethodChange}
                          >
                            <Translate value={option.labelKey} />
                            {option.comingSoon && (
                              <span className="profile-config-badge profile-config-badge--soon">
                                <Translate value="coming_soon" />
                              </span>
                            )}
                          </ConfigOption>
                        ))}
                      </div>
                      {this.renderTwoFactorMethodPanel()}
                    </>
                  )}

                  {twoFactorSettings.enabled && (
                    <p className="profile-config-active-hint">
                      <Translate value="profile_configuration_2fa_active_hint" />
                    </p>
                  )}

                  {twoFactorLoading && ready && (
                    <div className="profile-config-loading">
                      <MiniLoader />
                    </div>
                  )}
                </section>

                <UserVerificationPanel
                  user_id={this.props.user_id}
                  token={this.props.token}
                />

                <section className="profile-data-section profile-config-danger">
                  <h4 className="profile-data-section__title">
                    <Translate value="profile_configuration_delete_account" />
                  </h4>
                  <p className="profile-config-danger__hint">
                    <Translate value="profile_configuration_delete_hint" />
                  </p>
                  <div className="profile-config-danger__form">
                    <input
                      type="password"
                      className="form-control profile-edit-input"
                      name="deletePassword"
                      autoComplete="current-password"
                      placeholder={I18n.t("profile_configuration_delete_password_placeholder")}
                      value={this.state.deletePassword}
                      disabled={this.state.deletingAccount}
                      onChange={(event) =>
                        this.setState({ deletePassword: event.target.value })
                      }
                    />
                    <button
                      type="button"
                      className="profile-config-btn profile-config-btn--danger"
                      onClick={this.handleDeleteAccount}
                      disabled={this.state.deletingAccount}
                    >
                      <Translate value="profile_configuration_delete_btn" />
                    </button>
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </ProfileLayout>
    );
  }
}

function mapStateToProps(reducer) {
  return {
    user_id: reducer.session.auth.id,
    token: reducer.session.auth.access_token,
    profile: reducer.session.auth,
    userEmail: reducer.session.auth.email,
    userPhone: reducer.session.auth.phone,
    twoFactorSettings: reducer.twoFactor.settings,
    twoFactorErrorUpdate: reducer.twoFactor.error_update,
    twoFactorLoading: reducer.twoFactor.loading,
    codeSent: reducer.twoFactor.codeSent,
    qrReady: reducer.twoFactor.qrReady,
    sessionErrorUpdate: reducer.session.error_update,
  };
}

const mapDispatchToProps = {
  setLocalePreferenceAndApply,
  LoadTwoFactor: (user_id, token) =>
    twoFactorActions.loadTwoFactor({ user_id, token }),
  UpdateTwoFactor: (data) => twoFactorActions.updateTwoFactor(data),
  SendTwoFactorCode: (data) => twoFactorActions.sendTwoFactorCode(data),
  SetupTwoFactorAuthenticator: (user_id, token) =>
    twoFactorActions.setupTwoFactorAuthenticator({ user_id, token }),
  VerifyTwoFactor: (data) => twoFactorActions.verifyTwoFactor(data),
  DeleteAccount: (data) => sessionActions.deleteAccount(data),
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ProfileConfiguration)
);
